import { hasSubscriptionPlan } from './plan';
import {
  LOGOUT_PATH,
  PAYMENT_SUCCESS_PATH,
  PROFILE_SUBSCRIPTION_PATH,
  SUBSCRIPTION_EXPIRED_PATH,
  ORGANIZATION_STRIPE_PAYMENT_SUCCESS_PATH,
  ORGANIZATION_LICENSE_CHANGE_CONFIRM_PATH,
} from '../constants/pagePath';

const EXPIRED_END_MS_STORAGE_KEY = 'subscriptionExpiredPeriodEndMs';

const pick = (obj, camel, pascal) =>
  obj && Object.prototype.hasOwnProperty.call(obj, camel) ? obj[camel] : obj?.[pascal];

const readPersistedSubscriptionEndMs = () => {
  try {
    const raw = sessionStorage.getItem(EXPIRED_END_MS_STORAGE_KEY);
    if (!raw) return null;
    const ms = Number(raw);
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
};

/** Remember the period end once expired so lockout survives API reads that omit the date. */
export const persistSubscriptionEndMs = (endMs) => {
  if (endMs == null || Number.isNaN(endMs)) return;
  try {
    sessionStorage.setItem(EXPIRED_END_MS_STORAGE_KEY, String(endMs));
  } catch {
    // ignore quota / private mode
  }
};

export const clearPersistedSubscriptionEndMs = () => {
  try {
    sessionStorage.removeItem(EXPIRED_END_MS_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const toEndMs = (raw) => {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
};

const resolveSubscriptionEndMs = (user, subscriptions) => {
  const plans = Array.isArray(subscriptions) ? subscriptions : [];
  const currentPlanId = user?.currentPlan ?? user?.CurrentPlan;
  const current = plans.find(
    (p) => pick(p, 'isCurrent', 'IsCurrent') || p.id === currentPlanId
  );
  const fromSubscriptionPlan = toEndMs(
    pick(current, 'subscriptionPeriodEndUtc', 'SubscriptionPeriodEndUtc')
  );

  const fromStripeSnapshot = toEndMs(
    pick(user, 'subscriptionPeriodEndUtc', 'SubscriptionPeriodEndUtc')
  );
  const fromPlanEnd = toEndMs(pick(user, 'currentPlanEndDate', 'CurrentPlanEndDate'));
  const persisted = readPersistedSubscriptionEndMs();

  const now = Date.now();
  const candidates = [fromStripeSnapshot, fromPlanEnd, fromSubscriptionPlan, persisted].filter(
    (ms) => ms != null
  );
  const futureCandidates = candidates.filter((ms) => ms > now);
  if (futureCandidates.length > 0) {
    clearPersistedSubscriptionEndMs();
    return Math.max(...futureCandidates);
  }

  if (fromStripeSnapshot != null) return fromStripeSnapshot;
  if (fromPlanEnd != null) return fromPlanEnd;
  if (fromSubscriptionPlan != null) return fromSubscriptionPlan;
  return persisted;
};

/** Paid plan whose billing period end timestamp has passed. */
export const isSubscriptionExpired = (user, subscriptions) => {
  if (!user || !hasSubscriptionPlan(user.currentPlan ?? user.CurrentPlan)) {
    clearPersistedSubscriptionEndMs();
    return false;
  }

  const endMs = resolveSubscriptionEndMs(user, subscriptions);
  if (endMs == null) return false;

  const expired = endMs < Date.now();
  if (expired) {
    persistSubscriptionEndMs(endMs);
  } else {
    clearPersistedSubscriptionEndMs();
  }

  return expired;
};

/** Resolved period end for display (includes persisted fallback while expired). */
export const getSubscriptionPeriodEndMs = (user, subscriptions) =>
  resolveSubscriptionEndMs(user, subscriptions);

const LOCKOUT_EXEMPT_PATHS = [
  SUBSCRIPTION_EXPIRED_PATH,
  PROFILE_SUBSCRIPTION_PATH,
  PAYMENT_SUCCESS_PATH,
  LOGOUT_PATH,
  ORGANIZATION_STRIPE_PAYMENT_SUCCESS_PATH,
  ORGANIZATION_LICENSE_CHANGE_CONFIRM_PATH,
];

export const isLockoutExemptPath = (pathname) =>
  LOCKOUT_EXEMPT_PATHS.some(
    (path) => pathname === path || pathname?.toLowerCase() === path.toLowerCase()
  );

/** Redirect authenticated users with an expired paid plan to the lockout page. */
export const shouldRedirectToSubscriptionExpired = (user, subscriptions, pathname) => {
  if (!isSubscriptionExpired(user, subscriptions)) return false;
  if (isLockoutExemptPath(pathname)) return false;
  return true;
};

/** @deprecated Use shouldRedirectToSubscriptionExpired for routing; modal uses forceOpen on lockout page. */
export const shouldShowSubscriptionLockout = (user, subscriptions, pathname) => {
  if (!isSubscriptionExpired(user, subscriptions)) return false;
  return pathname === SUBSCRIPTION_EXPIRED_PATH || isLockoutExemptPath(pathname) === false;
};
